import { useState } from 'react';
import { Save } from 'lucide-react';
import toast from 'react-hot-toast';
import { Modal } from '@/components/ui/Modal';
import { useMutation } from '@/lib/api-hooks';
import { backendApi } from '@/lib/backend';
import { ExtensionPicker, Field } from './fields';
import { buttonClass, errorMessage, inputClass } from './format';
import type { CrmTask } from './types';

function toLocalInput(ms?: number) {
  if (!ms) return '';
  const date = new Date(ms);
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/** Create a follow-up (for a contact or company) or edit any task's title, deadline and assignee. */
export function TaskEditor({
  task,
  contactId,
  companyId,
  subjectName,
  onClose,
}: {
  task?: CrmTask | null;
  contactId?: string;
  companyId?: string;
  subjectName?: string;
  onClose: () => void;
}) {
  const saveTask = useMutation(backendApi.crm.saveTask);
  const [saving, setSaving] = useState(false);
  const tomorrowNine = (() => {
    const date = new Date(Date.now() + 86_400_000);
    date.setHours(9, 0, 0, 0);
    return date.getTime();
  })();
  const [form, setForm] = useState({
    title: task?.title ?? '',
    details: task?.details ?? '',
    due: toLocalInput(task ? task.dueAtMs : tomorrowNine),
    priority: task?.priority ?? ('normal' as 'normal' | 'high'),
    assigneeExtension: task?.assigneeExtension ?? '',
    assigneeName: task?.assigneeName ?? '',
  });

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!form.title.trim()) {
      toast.error('A task needs a title');
      return;
    }
    setSaving(true);
    try {
      await saveTask({
        taskId: task?.id,
        title: form.title,
        details: form.details || undefined,
        contactId: task?.contactId ?? contactId,
        companyId: task?.companyId ?? companyId,
        dueAtMs: form.due ? new Date(form.due).getTime() : undefined,
        priority: form.priority,
        assigneeExtension: form.assigneeExtension || undefined,
        assigneeName: form.assigneeName || undefined,
      });
      toast.success(task ? 'Task updated' : 'Task added');
      onClose();
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen onClose={onClose} title={task ? 'Edit task' : `New follow-up${subjectName ? ` · ${subjectName}` : ''}`}>
      <form onSubmit={submit} className="space-y-4">
        <Field label="What needs doing *" htmlFor="task-title">
          <input id="task-title" autoFocus value={form.title} onChange={(event) => setForm({ ...form, title: event.target.value })} className={inputClass} placeholder="Send the quote" />
        </Field>
        <Field label="Details" htmlFor="task-details">
          <textarea id="task-details" rows={3} value={form.details} onChange={(event) => setForm({ ...form, details: event.target.value })} className={inputClass} />
        </Field>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Due" htmlFor="task-due">
            <input id="task-due" type="datetime-local" value={form.due} onChange={(event) => setForm({ ...form, due: event.target.value })} className={inputClass} />
          </Field>
          <Field label="Priority" htmlFor="task-priority">
            <select id="task-priority" value={form.priority} onChange={(event) => setForm({ ...form, priority: event.target.value as 'normal' | 'high' })} className={inputClass}>
              <option value="normal">Normal</option>
              <option value="high">High</option>
            </select>
          </Field>
        </div>
        <Field label="Assigned to" htmlFor="task-assignee">
          <ExtensionPicker
            id="task-assignee"
            value={form.assigneeExtension}
            onChange={(extension, name) => setForm({ ...form, assigneeExtension: extension, assigneeName: name ?? '' })}
          />
        </Field>
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className={buttonClass.secondary}>
            Cancel
          </button>
          <button type="submit" disabled={saving} className={buttonClass.primary}>
            <Save className="w-4 h-4" /> {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
